import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ClFormHelperText from '../ClFormHelperText/ClFormHelperText.vue';
import ClIcon from '../ClIcon/ClIcon.vue';
import ClIconButton from '../ClIconButton/ClIconButton.vue';
import ClFormLabel from '../ClFormLabel/ClFormLabel.vue';
import ClTypography from '../ClTypography/ClTypography.vue';

@Component({
  components: {
    ClIcon,
    ClIconButton,
    ClFormLabel,
    ClTypography,
    ClFormHelperText
  }
})
export default class ClCheckbox extends Vue {
  /**
   * If `true`, the component is checked.
   * If this is set to either true or false explicity,
   * the assumption is that it's checked state is controlled externally.
   */
  @Prop({
    type: Boolean,
    default: null
  })
  public checked?: boolean;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public clIconProps?: object;

  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  @Prop({
    type: String,
    validator: x => ['primary', 'secondary', 'default'].includes(x),
    default: 'secondary'
  })
  public color!: string;

  /**
   * If `true`, the component is checked by default,
   * while it's checked state is controlled internally.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public defaultChecked!: boolean;

  /**
   * If `true`, the switch will be disabled.
   */
  @Prop(Boolean)
  public disabled!: boolean;

  /**
   * If `true`, the switch will be in an error state.
   */
  @Prop(Boolean)
  public error!: boolean;

  /**
   * Value when unchecked
   */
  @Prop({
    type: [String, Number, Boolean],
    default: false
  })
  public falseValue!: string | number | boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public indeterminate!: boolean;

  /**
   * If `true`, `checkedState` will be controlled externally.
   */
  public isControlled: boolean = this.checked !== null;

  /**
   * The id of the `input` element.
   */
  @Prop(String)
  public id!: string;

  /**
   * Props for the input element
   */
  @Prop(Object)
  public inputProps!: object;

  /**
   * Value when checked
   */
  @Prop({
    type: [String, Number, Boolean],
    default: true
  })
  public trueValue!: string | number | boolean;

  /**
   * The value of the component.
   */
  @Prop({
    type: [String, Number, Boolean]
  })
  public value?: string | boolean | number;

  /**
   * Checked state, for use when component is not controlled externally.
   */
  private checkedState: boolean =
    this.checked || this.defaultChecked || this.value === this.trueValue;

  /**
   * Watch if the value changes and change the checkedState as a result.
   */
  @Watch('value')
  handleValueChange(n: string | boolean | number) {
    this.checkedState = n === this.trueValue;
  }

  @Watch('checked')
  handleCheckedChange(n: boolean) {
    this.checkedState = n;
  }

  /**
   * We maintain the internal checked state if the checked prop isnt set externally
   */
  public handleChange(event: Event) {
    this.$emit('change', event);
    const { checked } = event.target as HTMLInputElement;
    if (this.isControlled) {
      this.checkedState = this.checked || false;
      return;
    }
    this.checkedState = checked;
  }

  public handleInput(event: Event) {
    const { checked } = event.target as HTMLInputElement;
    this.$emit('input', checked ? this.trueValue : this.falseValue);
  }
}
